import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';
import ExercicioGratisNavbar from '../components/ExercicioGratisNavbar';
import LeadCaptureForm from '../components/LeadCaptureForm';

export default function ProsperidadeCarreiraPage() {
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [financialGoal, setFinancialGoal] = useState('');

  const goals = [
    { id: 'renda', label: 'Aumentar Renda Mensal', icon: 'ri-money-dollar-circle-line', color: 'bg-emerald-100 text-emerald-600' },
    { id: 'promocao', label: 'Conquistar Promoção', icon: 'ri-arrow-up-circle-line', color: 'bg-green-100 text-green-600' },
    { id: 'negocio', label: 'Criar Negócio Próprio', icon: 'ri-store-3-line', color: 'bg-teal-100 text-teal-600' },
    { id: 'investimentos', label: 'Multiplicar Investimentos', icon: 'ri-line-chart-line', color: 'bg-cyan-100 text-cyan-600' },
    { id: 'liberdade', label: 'Liberdade Financeira', icon: 'ri-flight-takeoff-line', color: 'bg-blue-100 text-blue-600' },
    { id: 'abundancia', label: 'Mentalidade de Abundância', icon: 'ri-sparkling-line', color: 'bg-indigo-100 text-indigo-600' },
    { id: 'patrimonio', label: 'Construir Patrimônio', icon: 'ri-building-line', color: 'bg-violet-100 text-violet-600' },
    { id: 'passiva', label: 'Renda Passiva', icon: 'ri-time-line', color: 'bg-purple-100 text-purple-600' },
  ];

  const toggleGoal = (id: string) => setSelectedGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <SEOHead
        title="Visualização de Prosperidade e Carreira Grátis | Abundância — Carta do Eu Futuro"
        description="Experimente gratuitamente a Visualização de Prosperidade e Carreira. Manifeste abundância financeira e sucesso profissional através da reprogramação mental e PNL."
        keywords="prosperidade, carreira, abundância financeira, PNL grátis, manifestação"
        canonical="/exercicio-gratis/prosperidade-carreira"
      />
      <ExercicioGratisNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10 text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"><i className="ri-gift-line"></i>VERSÃO GRATUITA</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent leading-tight">Visualização de Prosperidade e Carreira</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">Manifeste abundância financeira e sucesso profissional através da visualização criativa e reprogramação mental</p>
        </div>
      </section>

      {/* O que é */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">O que é a Visualização de Prosperidade?</h2>
          <p className="text-lg leading-relaxed mb-6 text-gray-700">A <strong>Visualização de Prosperidade e Carreira</strong> combina neurociência, PNL e manifestação para reprogramar sua mente subconsciente em relação ao dinheiro e sucesso.</p>
          <p className="text-lg leading-relaxed text-gray-700">Ao visualizar com detalhes sua vida financeira ideal, você cria novos padrões neurais que atraem oportunidades, ideias e ações alinhadas com seus objetivos.</p>
        </div>
      </section>

      {/* Exercício */}
      <section className="py-16 px-4 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><i className="ri-gift-line"></i>EXPERIMENTE GRÁTIS</div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Exercício Introdutório</h2>
            </div>
            <h3 className="text-xl font-bold mb-6 text-gray-900">Selecione seus objetivos financeiros principais:</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {goals.map((goal) => (
                <button key={goal.id} onClick={() => toggleGoal(goal.id)} className={`p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${selectedGoals.includes(goal.id) ? 'border-emerald-500 bg-emerald-50 scale-105' : 'border-gray-200 bg-white hover:border-emerald-300'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${goal.color} rounded-lg flex items-center justify-center`}><i className={`${goal.icon} text-xl`}></i></div>
                    <span className="font-semibold text-gray-900">{goal.label}</span>
                    {selectedGoals.includes(goal.id) && <i className="ri-checkbox-circle-fill text-emerald-500 text-xl ml-auto"></i>}
                  </div>
                </button>
              ))}
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Defina sua meta financeira específica:</h3>
            <input type="text" value={financialGoal} onChange={(e) => setFinancialGoal(e.target.value)} placeholder="Ex: Ganhar R$ 10.000 por mês até dezembro de 2025" className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-emerald-500 outline-none text-gray-900 text-sm" />
            {(selectedGoals.length > 0 || financialGoal) && <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200"><p className="text-sm text-emerald-700 font-medium">Ótimo começo! Você está dando o primeiro passo para manifestar sua prosperidade!</p></div>}
            {/* Bloqueados */}
            <div className="relative mt-8">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white p-8 rounded-2xl text-center max-w-sm">
                  <i className="ri-vip-crown-line text-5xl mb-4 block"></i>
                  <h3 className="text-2xl font-bold mb-3">Desbloqueie o Exercício Completo</h3>
                  <p className="text-emerald-100 mb-6">7 passos completos + áudio guiado + workbook</p>
                  <Link to="/planos" className="bg-white text-emerald-600 px-8 py-4 rounded-xl font-bold hover:bg-emerald-50 transition-all whitespace-nowrap block">Ver Planos — A partir de R$ 18</Link>
                </div>
              </div>
              <div className="opacity-30 pointer-events-none space-y-4">
                {[3,4,5,6,7].map((n) => <div key={n} className="p-6 bg-gray-50 rounded-xl border-2 border-gray-200"><h3 className="font-bold text-gray-600">Passo {n} — bloqueado</h3></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureForm exercicioNome="Prosperidade e Carreira" />

      {/* CTA Final */}
      <section className="py-20 px-4 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-12 h-px bg-gold-500/30 mx-auto mb-8"></div>
          <i className="ri-money-dollar-circle-fill text-5xl mb-6 opacity-60 block text-gold-400"></i>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6 text-white">Pronto para Manifestar Prosperidade e Abundância?</h2>
          <p className="text-xl text-white/55 mb-8 max-w-2xl mx-auto font-['Montserrat'] font-light">Desbloqueie o exercício completo e comece sua transformação financeira hoje</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/planos" className="bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 px-10 py-5 rounded-xl font-bold hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] tracking-wide">Ver Planos e Preços</Link>
            <Link to="/" className="bg-white/8 text-white px-10 py-5 rounded-xl font-bold hover:bg-white/14 transition-all border border-gold-500/20 whitespace-nowrap font-['Montserrat'] tracking-wide">Voltar ao Início</Link>
          </div>
          <p className="text-white/30 text-sm mt-6 font-['Montserrat']">Garantia de 7 dias · Acesso vitalício</p>
        </div>
      </section>
    </div>
  );
}
